<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import CSATReports from 'dashboard/api/csatReports';
import Pagination from 'dashboard/components/table/Pagination.vue';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { CSAT_RATINGS } from 'shared/constants/messages';
import ReportLineChart from '../ReportLineChart.vue';
import ReportCard from '../ReportCard.vue';
import { getCoreRowModel, useVueTable } from '@tanstack/vue-table';
import { getLineChartFilters } from 'dashboard/helper/reportTimeRangeHelper';

const props = defineProps({
  metrics: {
    type: Object,
    default: () => ({}),
  },
  responses: {
    type: Array,
    default: () => [],
  },
  pageIndex: {
    type: Number,
    default: 0,
  },
  satisfactionScore: {
    type: [Number, String],
    default: 0,
  },
  responseRate: {
    type: [Number, String],
    default: 0,
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  isFetchingMetrics: {
    type: Boolean,
    default: false,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['pageChange']);

const chartResponses = ref([]);
const isFetchingChart = ref(false);

const ratingConfig = {
  5: { label: 'Excelente', emoji: '😍', color: '#44CE4B' },
  4: { label: 'Bom', emoji: '😀', color: '#6FD86F' },
  3: { label: 'Neutro', emoji: '😐', color: '#FCEC56' },
  2: { label: 'Ruim', emoji: '😟', color: '#FFC532' },
  1: { label: 'Péssimo', emoji: '😡', color: '#FDAD2A' },
};

const totalResponses = computed(() => props.metrics.totalResponseCount || 0);
const paginationState = computed(() => ({
  pageIndex: props.pageIndex,
  pageSize: 25,
}));

const paginationTable = useVueTable({
  get data() {
    return props.responses;
  },
  columns: [],
  manualPagination: true,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return totalResponses.value;
  },
  state: {
    get pagination() {
      return paginationState.value;
    },
  },
  onPaginationChange: updater => {
    const pagination = updater(paginationState.value);
    emit('pageChange', pagination.pageIndex);
  },
});

const totalSent = computed(() => props.metrics.totalSentMessagesCount || 0);
const score = computed(() => Math.round(Number(props.satisfactionScore) || 0));
const rate = computed(() => Math.round(Number(props.responseRate) || 0));
const responseGaugeStyle = computed(() => ({
  background: `conic-gradient(#1f93ff ${Math.min(Math.max(rate.value, 0), 100) * 3.6}deg, rgba(31, 147, 255, 0.16) 0deg)`,
}));

const distributionRows = computed(() =>
  [5, 4, 3, 2, 1].map(value => {
    const count = props.metrics.ratingsCount?.[value] || 0;
    return {
      value,
      count,
      percent: totalResponses.value
        ? Math.round((count / totalResponses.value) * 100)
        : 0,
      ...ratingConfig[value],
    };
  })
);

const positiveResponses = computed(
  () =>
    (props.metrics.ratingsCount?.[4] || 0) +
    (props.metrics.ratingsCount?.[5] || 0)
);

const scoreLabel = computed(() => {
  if (score.value >= 90) return 'Excelente';
  if (score.value >= 70) return 'Bom';
  if (score.value >= 50) return 'Neutro';
  return 'Atenção';
});

const agentRows = computed(() => {
  return (props.metrics.agentMetrics || [])
    .map(agent => ({
      ...agent,
      key: agent.id || 'unassigned',
      name: agent.name || 'Não atribuído',
      percent: agent.count
        ? Math.round((agent.rating_sum / (agent.count * 5)) * 100)
        : 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
});

const evolutionRows = computed(() => {
  const grouped = chartResponses.value.reduce((acc, response) => {
    const date = fromUnixTime(response.created_at);
    const key = format(date, 'yyyy-MM-dd');
    if (!acc[key]) {
      acc[key] = {
        label: format(date, 'dd/MM'),
        timestamp: response.created_at,
        total: 0,
        ratingSum: 0,
      };
    }
    acc[key].total += 1;
    acc[key].ratingSum += Number(response.rating);
    return acc;
  }, {});

  return Object.values(grouped).sort((a, b) => a.timestamp - b.timestamp);
});

const chartCollection = computed(() => {
  const rows = evolutionRows.value;
  return {
    labels: rows.map(row => row.label),
    datasets: [
      {
        label: 'CSAT (%)',
        data: rows.map(row =>
          row.total ? Math.round((row.ratingSum / (row.total * 5)) * 100) : 0
        ),
        borderColor: '#44CE4B',
        backgroundColor: '#44CE4B',
        yAxisID: 'y',
      },
      {
        label: 'Respostas',
        data: rows.map(row => row.total),
        borderColor: '#1f93ff',
        backgroundColor: '#1f93ff',
        yAxisID: 'responses',
      },
    ],
  };
});

const chartOptions = {
  scales: {
    y: {
      min: 0,
      max: 100,
      ticks: { callback: value => `${value}%` },
    },
    responses: {
      beginAtZero: true,
      position: 'right',
      grid: { drawOnChartArea: false },
    },
  },
};

const ratingFor = rating =>
  CSAT_RATINGS.find(item => item.value === Number(rating)) || {};

const conversationRouteParams = conversationId => ({
  name: 'inbox_conversation',
  params: { conversation_id: conversationId },
});

const createdAtLabel = createdAt => {
  if (!createdAt) return '';
  return format(fromUnixTime(createdAt), 'dd/MM/yyyy HH:mm');
};

const initialsFor = name => {
  if (!name) return '--';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const fetchChartResponses = async () => {
  if (!props.filters.from || !props.filters.to) return;

  const chartFilters = getLineChartFilters(props.filters);
  const filterParams = {
    from: chartFilters.from,
    to: chartFilters.to,
    user_ids: props.filters.user_ids,
    inbox_id: props.filters.inbox_id,
    team_id: props.filters.team_id,
    rating: props.filters.rating,
    conversationDirection: props.filters.conversationDirection,
    comunicanteCampaign: props.filters.comunicanteCampaign,
  };

  isFetchingChart.value = true;
  try {
    const fetchPage = async (page, records = []) => {
      const response = await CSATReports.get({ page, ...filterParams });
      const pageRecords = Array.isArray(response.data) ? response.data : [];
      const allRecords = [...records, ...pageRecords];

      if (pageRecords.length === 25 && page < 100) {
        return fetchPage(page + 1, allRecords);
      }
      return allRecords;
    };

    chartResponses.value = await fetchPage(1);
    await nextTick();
  } catch {
    chartResponses.value = [];
    await nextTick();
  } finally {
    isFetchingChart.value = false;
  }
};

watch(
  () => [
    props.filters.from,
    props.filters.to,
    props.filters.user_ids,
    props.filters.inbox_id,
    props.filters.team_id,
    props.filters.rating,
    props.filters.conversationDirection,
    props.filters.comunicanteCampaign,
  ],
  fetchChartResponses,
  { deep: true }
);

onMounted(() => {
  fetchChartResponses();
});
</script>

<template>
  <div class="space-y-4">
    <div class="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <ReportCard
        class="xl:col-span-5 !border-green-500/40"
        :is-loading="isFetchingMetrics"
      >
        <p class="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
          CSAT Geral
        </p>
        <div class="flex items-center justify-between gap-4">
          <div>
            <div class="flex items-center gap-3">
              <strong class="text-5xl text-slate-900 dark:text-slate-50">
                {{ score }}%
              </strong>
              <span class="text-3xl">😍</span>
              <span
                class="px-3 py-1 text-sm font-medium text-green-700 rounded-full bg-green-100 dark:text-green-200 dark:bg-green-900"
              >
                {{ scoreLabel }}
              </span>
            </div>
            <p class="mt-3 mb-0 text-sm text-slate-600 dark:text-slate-300">
              {{ totalResponses }} respostas recebidas
            </p>
          </div>
          <div class="csat-check">
            <fluent-icon icon="checkmark" size="36" />
          </div>
        </div>
      </ReportCard>

      <ReportCard class="xl:col-span-3" :is-loading="isFetchingMetrics">
        <p class="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
          Respostas
        </p>
        <strong class="text-4xl text-slate-900 dark:text-slate-50">
          {{ totalResponses }}
        </strong>
        <p class="mt-2 mb-0 text-sm text-slate-500 dark:text-slate-400">
          {{ totalResponses < 10 ? 'baixa amostra' : 'amostra consistente' }}
        </p>
      </ReportCard>

      <ReportCard class="xl:col-span-4" :is-loading="isFetchingMetrics">
        <div class="flex items-center justify-between gap-5">
          <div>
            <p
              class="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Taxa de resposta
            </p>
            <strong class="text-4xl text-slate-900 dark:text-slate-50">
              {{ rate }}%
            </strong>
            <p class="mt-2 mb-0 text-sm text-slate-500 dark:text-slate-400">
              {{ totalResponses }} respostas de {{ totalSent }} pesquisas
              enviadas
            </p>
          </div>
          <div class="csat-rate-gauge" :style="responseGaugeStyle">
            <span class="csat-rate-gauge__center">
              <fluent-icon icon="people" size="30" />
            </span>
          </div>
        </div>
      </ReportCard>
    </div>

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <ReportCard class="xl:col-span-5" :is-loading="isFetchingMetrics">
        <h3 class="mb-4 text-lg font-medium text-slate-900 dark:text-slate-50">
          Distribuição das avaliações
        </h3>
        <div class="space-y-3">
          <div
            v-for="row in distributionRows"
            :key="row.value"
            class="grid items-center grid-cols-[7.5rem_1fr_2rem_3rem] gap-3 text-sm"
          >
            <span class="text-slate-700 dark:text-slate-200">
              {{ row.emoji }} {{ row.label }}
            </span>
            <span
              class="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
            >
              <span
                class="block h-full rounded-full"
                :style="{
                  width: `${row.percent}%`,
                  backgroundColor: row.color,
                }"
              />
            </span>
            <span class="text-right text-slate-700 dark:text-slate-200">
              {{ row.count }}
            </span>
            <span class="text-right text-slate-500 dark:text-slate-400">
              {{ row.percent }}%
            </span>
          </div>
        </div>
      </ReportCard>

      <ReportCard class="csat-summary xl:col-span-7">
        <h3
          class="relative z-10 mb-3 text-lg font-medium text-slate-900 dark:text-slate-50"
        >
          Resumo do CSAT
        </h3>
        <p
          class="relative z-10 max-w-3xl text-sm text-slate-700 dark:text-slate-200"
        >
          O período teve {{ score }}% de satisfação. {{ positiveResponses }} de
          {{ totalResponses }} respostas foram positivas.
        </p>
        <div
          class="relative z-10 pt-4 mt-4 border-t border-solid border-slate-75 dark:border-slate-700"
        >
          <p class="mb-0 text-sm text-slate-600 dark:text-slate-300">
            Ponto de atenção: a taxa de resposta foi de {{ rate }}%, então o
            resultado depende da amostra recebida no período.
          </p>
        </div>
      </ReportCard>
    </div>

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <ReportCard class="xl:col-span-5" :is-loading="isFetchingMetrics">
        <h3 class="mb-4 text-lg font-medium text-slate-900 dark:text-slate-50">
          CSAT por agente
        </h3>
        <div class="space-y-3">
          <div
            v-for="agent in agentRows"
            :key="agent.key"
            class="grid items-center grid-cols-[1fr_5rem_1fr_3rem] gap-3 text-sm"
          >
            <span
              class="flex items-center gap-2 text-slate-800 dark:text-slate-100"
            >
              <span class="csat-avatar">{{ initialsFor(agent.name) }}</span>
              {{ agent.name }}
            </span>
            <span class="text-slate-500 dark:text-slate-400">
              {{ agent.count }} respostas
            </span>
            <span
              class="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
            >
              <span
                class="block h-full bg-green-500 rounded-full"
                :style="{ width: `${agent.percent}%` }"
              />
            </span>
            <span class="text-right text-slate-700 dark:text-slate-200">
              {{ agent.percent }}%
            </span>
          </div>
          <p
            v-if="!agentRows.length"
            class="mb-0 text-sm text-slate-500 dark:text-slate-400"
          >
            Sem respostas por agente no período.
          </p>
        </div>
      </ReportCard>

      <ReportCard class="xl:col-span-7" :is-loading="isFetchingChart">
        <div class="mb-4">
          <h3
            class="mb-0 text-lg font-medium text-slate-900 dark:text-slate-50"
          >
            Evolução diária
          </h3>
        </div>
        <div v-if="evolutionRows.length" class="w-full h-52">
          <ReportLineChart
            :collection="chartCollection"
            :chart-options="chartOptions"
          />
        </div>
        <div
          v-else
          class="flex items-center justify-center h-52 text-sm text-slate-500 dark:text-slate-400"
        >
          Sem dados suficientes para o gráfico.
        </div>
      </ReportCard>
    </div>

    <ReportCard :is-loading="isFetching">
      <div class="mb-3">
        <h3 class="mb-0 text-lg font-medium text-slate-900 dark:text-slate-50">
          Feedbacks recebidos
        </h3>
      </div>
      <div class="overflow-hidden rounded-md bg-slate-25 dark:bg-slate-900">
        <table class="w-full text-sm">
          <thead class="text-left text-slate-500 dark:text-slate-400">
            <tr>
              <th class="px-3 py-2 font-medium">Cliente</th>
              <th class="px-3 py-2 font-medium">Agente</th>
              <th class="px-3 py-2 font-medium">Avaliação</th>
              <th class="px-3 py-2 font-medium">Comentário</th>
              <th class="px-3 py-2 font-medium text-right">Horário</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-75 dark:divide-slate-800">
            <tr
              v-for="response in responses"
              :key="response.id"
              class="text-slate-700 dark:text-slate-200"
            >
              <td class="px-3 py-2">
                {{ response.contact?.name || '---' }}
              </td>
              <td class="px-3 py-2">
                {{ response.assigned_agent?.name || '---' }}
              </td>
              <td class="px-3 py-2">
                <span
                  class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 rounded-full bg-green-100 dark:text-green-200 dark:bg-green-900"
                >
                  {{ ratingFor(response.rating).emoji }}
                  {{ ratingConfig[response.rating]?.label || '---' }}
                </span>
              </td>
              <td class="px-3 py-2">
                {{ response.feedback_message || '---' }}
              </td>
              <td
                class="px-3 py-2 text-right text-slate-500 dark:text-slate-400"
              >
                <div class="flex flex-col items-end">
                  <router-link
                    v-if="response.conversation_id"
                    :to="conversationRouteParams(response.conversation_id)"
                    target="_blank"
                    class="hover:underline text-woot-500 dark:text-woot-500"
                  >
                    {{ `#${response.conversation_id}` }}
                  </router-link>
                  <span
                    v-tooltip="createdAtLabel(response.created_at)"
                    class="text-sm"
                  >
                    {{ dynamicTime(response.created_at) }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div
          v-if="!responses.length"
          class="flex items-center justify-center h-32 text-sm text-slate-500 dark:text-slate-400"
        >
          Não há respostas de CSAT no período.
        </div>
      </div>
      <Pagination v-if="totalResponses" class="mt-3" :table="paginationTable" />
    </ReportCard>
  </div>
</template>

<style lang="scss" scoped>
.csat-check {
  @apply flex items-center justify-center w-20 h-20 text-green-500 border-4 border-solid rounded-full border-green-500/40;
}

.csat-avatar {
  @apply flex items-center justify-center flex-shrink-0 w-6 h-6 text-xs font-medium text-white rounded-full bg-woot-500;
}

.csat-rate-gauge {
  @apply flex items-center justify-center flex-shrink-0 w-20 h-20 rounded-full;
}

.csat-rate-gauge__center {
  @apply flex items-center justify-center w-16 h-16 rounded-full text-woot-500 bg-white dark:bg-slate-800;
}

.csat-summary::after {
  position: absolute;
  top: 0;
  right: 0;
  width: 42%;
  height: 100%;
  content: '';
  opacity: 0.5;
  background-image: radial-gradient(
    circle,
    rgba(31, 147, 255, 0.6) 1px,
    transparent 1.5px
  );
  background-size: 15px 15px;
  mask-image: linear-gradient(to left, black, transparent);
}
</style>
